import { reduce } from "lodash";
import {  safeParseJSON } from "./utils/misc";

declare const jQuery: any;
const $ = jQuery;

export interface UploadProgressData
{
	fileIndex: number,
	totalFiles: number,
	file: File,
	fileProgress: number, // 0 <= n <= 1
	totalProgress: number, // 0 <= n <= 1
	totalBytesProgress: number,
	fileBytesProgress: number,
	totalBytes: number,
}

export interface UploadResult
{
	ok: boolean;
	serverData: any;
	files: File[];
	progress: UploadProgressData;
	statusCode: number;
	xhrEvent?: any;
}

// currentFileProgress & totalProgress are percentages rounded to nearest 1%
export type UploadProgressHandler = (data: UploadProgressData) => void;

// uploadHandlerName - corresponds to NAME const on PHP CustomFileUpload subclass
// Uploads one file at a time in sequence to avoid hitting server limits
// Returned Promise resolves immediately (no server request) if there are no files
// Resolves/rejects with UploadResult object
export function fileUpload(
	files: File[],
	uploadHandlerName: string,
	uploadData?: any,
	onProgress?: UploadProgressHandler): Promise<UploadResult>
{
	const progressData: UploadProgressData = {
		totalFiles: files.length,
		fileIndex: 0,
		file: files[0],
		fileBytesProgress: 0,
		fileProgress: 0, // float between 0 - 1
		totalProgress: 0, // float between 0 - 1
		totalBytesProgress: 0,
		totalBytes: reduce(files, (acc, file: File) => acc + file.size, 0),
	};

	if (files.length === 0)
	{
		console.warn("fileUpload warning: No files to upload");
		return Promise.resolve({
			ok: true,
			serverData: null,
			files,
			progress: progressData,
			statusCode: null,
			xhrEvent: null,
		});
	}

	return new Promise<UploadResult>((resolve, reject) => {
		const uploadDataJson: string = (uploadData === undefined) ? undefined : JSON.stringify(uploadData);
		
		const finalFileIndex: number = files.length - 1;

		const onFileSuccess = (result: UploadResult): void => {
			if (progressData.fileIndex === finalFileIndex)
			{
				resolve(result);
				return;
			}
			++progressData.fileIndex;
			progressData.file = files[progressData.fileIndex];
			uploadFile();
		};

		const uploadFile = () => {
			if (!(progressData.file instanceof File))
			{
				throw new Error(`Failed to find file at index ${progressData.fileIndex}`);
			}
			const formData = new FormData();
			formData.append("iw-cfi-ul-handler", uploadHandlerName);
			formData.append("iw-cfi-file[]", progressData.file);
			if (uploadDataJson)
			{
				formData.append("iw-cfi-ul-client-data", uploadDataJson);
			}
			const xhr = new XMLHttpRequest();
			xhr.upload.addEventListener("progress", function (e) {
				progressData.fileBytesProgress = e.loaded;
				progressData.fileProgress = e.loaded / e.total;
				progressData.totalBytesProgress += e.loaded;
				progressData.totalProgress = progressData.totalBytesProgress / progressData.totalBytes;
				if (typeof onProgress === "function")
				{
					onProgress(progressData);
				}
			});
			xhr.addEventListener("load", function (e) {
				const statusCode: number = e.target['status'];
				const serverData: any = safeParseJSON(xhr.responseText);
				if (statusCode < 400)
				{
					onFileSuccess({
						ok: true,
						serverData,
						files,
						progress: progressData,
						statusCode,
						xhrEvent: e,
					});
					return;
				}
				console.error("fileUpload xhr http error", xhr.status, xhr.statusText);
				xhr.abort();
				reject({
					ok: false,
					serverData,
					files,
					progress: progressData,
					statusCode,
					xhrEvent: e,
				});
			});
			xhr.addEventListener("error", e => {
				console.error("fileUpload xhr error", xhr.status, xhr.statusText);
				reject({
					ok: false,
					serverData: safeParseJSON(xhr.responseText),
					files,
					progress: progressData,
					statusCode: e.target['status'],
					xhrEvent: e,
				});
			});
			xhr.open("POST", "/wp-admin/"); // needs to be called after progress event listener add
			xhr.send(formData);
		};

		uploadFile();
	});
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].fileUpload = fileUpload;
